import { MarkEmailReadOutlined, Send as SendIcon, UploadFile as UploadFileIcon, Download as DownloadIcon } from "@mui/icons-material";
import { Alert, Box, Button, Chip, Divider, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { saveAs } from "file-saver";
import { useState } from "react";
import { useProfile } from "../../../../context/ProfileContext";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { maskCpf } from "../../../../shared/utils/globalMask-document";
import { useExcelImport } from "../../hooks/useExcelImport";
import { REQUIRED_HEADERS_CERTIFICATE, RowDataCertificate } from "../../types";
import { isValidCpf } from "../../utils/cpfValidator";
import { CERTIFICADO_STATUS_META } from "../../utils/certificadoStatus";
import { CertificadoResendModal } from "../CertificadoResendModal";
import { StatTile } from "../StatTile";

export const CertificadoTab = () => {
	const { isSuperAdmin, permissions, isPermissionInitialized } = useProfile();
	const [rows, setRows] = useState<RowDataCertificate[]>([]);
	const [loading, setLoading] = useState(false);
	const [fileInfo, setFileInfo] = useState<{ name: string; rowCount: number } | null>(null);
	const [missingFields, setMissingFields] = useState<string[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const canResendCertificateBulk =
		isPermissionInitialized &&
		(isSuperAdmin || !!permissions?.[PermissionRolesEnum.ToolsOptions]?.[PermissionActionsEnum.ResendCertificateBulk]);

	let currentFileName = "";

	const { handleFile } = useExcelImport({
		requiredHeaders: REQUIRED_HEADERS_CERTIFICATE,
		limit: 500,
		onStart: () => {
			setLoading(true);
			setRows([]);
			setMissingFields([]);
			setFileInfo(null);
		},
		onFinish: () => setLoading(false),
		onSuccess: (processed) => {
			const validated: RowDataCertificate[] = processed.map((row, index) => {
				const cpfDigits = String(row.cpf ?? "").replace(/\D/g, "");
				const valid = isValidCpf(cpfDigits);
				return {
					RowNumber: index,
					cpf: cpfDigits,
					status: valid ? "pendente" : "invalido",
					mensagem: valid ? "Aguardando processamento" : "CPF inválido — não será enviado",
				};
			});
			setRows(validated);
			setFileInfo({ name: currentFileName, rowCount: validated.length });
		},
		onError: (missing) => setMissingFields(missing),
	});

	const handleDownloadModelCSV = () => {
		const csv = "cpf\n12345678901\n";
		saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "modelo_certificado.csv");
	};

	const handleRowUpdate = (rowNumber: number, status: RowDataCertificate["status"], mensagem: string, proposalIds?: string[]) => {
		setRows((prev) => prev.map((row) => (row.RowNumber === rowNumber ? { ...row, status, mensagem, proposalIds } : row)));
	};

	const validRowsCount = rows.filter((row) => row.status !== "invalido").length;
	const invalidRowsCount = rows.length - validRowsCount;

	if (!canResendCertificateBulk) {
		return (
			<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={2} mx={1}>
				<Alert severity="warning">Você não possui permissão para utilizar a ferramenta de reenvio de certificado.</Alert>
			</Box>
		);
	}

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={2} mx={1}>
			{loading && <LoadingComp isopen={loading} />}

			<Stack direction="row" spacing={1.5} alignItems="center">
				<MarkEmailReadOutlined color="primary" />
				<Box>
					<Typography variant="h6" lineHeight={1.2}>
						Reenvio de Certificado
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Envie um CSV com uma coluna <strong>cpf</strong>. Cada CPF válido será usado para localizar a garantia ativa
						correspondente e reenviar o certificado ao parceiro.
					</Typography>
				</Box>
			</Stack>

			<Divider />

			<Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
				<Button variant="outlined" startIcon={<UploadFileIcon />} component="label">
					Importar CSV
					<input
						type="file"
						hidden
						accept=".csv"
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								currentFileName = file.name;
								handleFile(file);
								e.target.value = "";
							}
						}}
					/>
				</Button>

				<Button variant="text" startIcon={<DownloadIcon />} onClick={handleDownloadModelCSV}>
					Baixar Modelo
				</Button>

				<Box flexGrow={1} />

				<Button
					variant="contained"
					color="primary"
					startIcon={<SendIcon />}
					onClick={() => setIsModalOpen(true)}
					disabled={validRowsCount === 0}
				>
					Iniciar Reenvio ({validRowsCount})
				</Button>
			</Stack>

			{fileInfo && (
				<Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
					<StatTile label="Arquivo importado" value={fileInfo.rowCount} color="text.primary" />
					<StatTile label="CPFs válidos" value={validRowsCount} color="success.main" />
					<StatTile label="CPFs inválidos" value={invalidRowsCount} color="error.main" />
				</Stack>
			)}

			{fileInfo && (
				<Typography variant="caption" color="text.secondary">
					Arquivo: <strong>{fileInfo.name}</strong>
				</Typography>
			)}

			{missingFields.length > 0 && (
				<Alert severity="error">
					Campos obrigatórios ausentes: <strong>{missingFields.join(", ")}</strong>
				</Alert>
			)}

			{rows.length > 0 ? (
				<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 550 }}>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell>#</TableCell>
								<TableCell>CPF</TableCell>
								<TableCell>Situação</TableCell>
								<TableCell>Detalhes</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((row) => {
								const meta = CERTIFICADO_STATUS_META[row.status];
								return (
									<TableRow key={row.RowNumber} hover>
										<TableCell>#{(row.RowNumber + 1).toString().padStart(3, "0")}</TableCell>
										<TableCell>{maskCpf(row.cpf)}</TableCell>
										<TableCell>
											<Chip size="small" icon={meta.icon} label={meta.label} color={meta.color} variant="outlined" />
										</TableCell>
										<TableCell>
											<Typography variant="caption" color="text.secondary">
												{row.mensagem}
											</Typography>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			) : (
				<Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
					<UploadFileIcon sx={{ fontSize: 36, color: "text.disabled" }} />
					<Typography variant="body2" color="text.secondary">
						Nenhum arquivo importado ainda.
					</Typography>
				</Box>
			)}

			<CertificadoResendModal
				open={isModalOpen}
				rows={rows.filter((row) => row.status !== "invalido")}
				onClose={() => setIsModalOpen(false)}
				onRowUpdate={handleRowUpdate}
			/>
		</Box>
	);
};
